import { type ReactNode, useState } from "react";
import { styled } from "@pigment-css/react";
import { Search, SearchCloseButton } from "./Search";

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// Constant height across states so expanding the search never shifts the list below it.
const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[12],
  minHeight: theme.fieldSalesTwo.spacing[50],
}));

// The title (breakdown toggle / list title) collapses its width and fades as the search expands,
// mirroring the home TopBar's title↔search transition.
const TitleSlot = styled("div")({
  flex: "0 1 auto",
  minWidth: 0,
  marginRight: "auto",
  maxWidth: "100%",
  transition: `max-width 280ms ${EASE}, opacity 200ms ease`,
  // overflow:hidden only while collapsing — otherwise it would clip a dropdown opened from the title
  // (e.g. the nav dock's breakdown menu).
  '&[data-expanded="true"]': {
    maxWidth: 0,
    opacity: 0,
    overflow: "hidden",
    pointerEvents: "none",
  },
});

const SearchSlot = styled("div")(({ theme }) => ({
  display: "flex",
  minWidth: 0,
  flex: "0 1 auto",
  transition: `flex-grow 280ms ${EASE}`,
  '&[data-expanded="true"]': {
    flex: "1 1 auto",
  },
  // Non-bare + collapsed reads as a circle matching the close X: the underlying search pill is a
  // ~88px-wide capsule (Pill padding + icon), so override it to a padding-less 50px square. Bare mode
  // is already just an icon and needs no override. On expand the override lifts and the pill fills.
  '&[data-bare="false"]:not([data-expanded="true"])': {
    flex: "0 0 auto",
  },
  '&[data-bare="false"]:not([data-expanded="true"]) > *': {
    width: theme.fieldSalesTwo.spacing[50],
    height: theme.fieldSalesTwo.spacing[50],
    padding: 0,
    justifyContent: "center",
  },
}));

// The close button cross-fades in on expand; it holds no width while collapsed.
const CloseSlot = styled("div")({
  flex: "0 0 auto",
  overflow: "hidden",
  maxWidth: 0,
  opacity: 0,
  pointerEvents: "none",
  transition: `max-width 280ms ${EASE}, opacity 200ms ease`,
  '&[data-expanded="true"]': {
    maxWidth: "4rem",
    opacity: 1,
    pointerEvents: "auto",
  },
});

interface SearchHeaderRowProps {
  /** Leading content (e.g. the list title); hidden while the search is expanded. */
  title: ReactNode;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Notifies when the search input opens/closes, e.g. to keep the list mounted while open. */
  onExpandedChange?: (expanded: boolean) => void;
  /** Controlled open state; omit for uncontrolled (internal) state. */
  expanded?: boolean;
  /** Blocks opening the search, e.g. when there is nothing to search through. */
  disabled?: boolean;
  /** Collapsed search renders as a bare icon (inline list headers) vs. a glass pill (default). */
  bareSearch?: boolean;
}

export function SearchHeaderRow({
  title,
  placeholder,
  value,
  onChange,
  onExpandedChange,
  expanded: expandedProp,
  disabled,
  bareSearch = false,
}: SearchHeaderRowProps) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp ?? internalExpanded;

  const handleExpandedChange = (next: boolean) => {
    if (expandedProp === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
    if (!next) onChange("");
  };

  return (
    <Row>
      <TitleSlot data-expanded={expanded}>{title}</TitleSlot>
      <SearchSlot data-expanded={expanded} data-bare={bareSearch}>
        <Search
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          expanded={expanded}
          onExpandedChange={handleExpandedChange}
          disabled={disabled}
          bare={bareSearch}
        />
      </SearchSlot>
      <CloseSlot data-expanded={expanded} aria-hidden={!expanded}>
        <SearchCloseButton onClick={() => handleExpandedChange(false)} />
      </CloseSlot>
    </Row>
  );
}
